import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TiktokDateTimePicker from '@/tiktok-platform/modules/products/components/TiktokDateTimePicker';
import { formatTiktokDateTime } from '@/tiktok-platform/utils/tiktokDateRange';
import { showToast } from '@/utils/toast';

function buildRange(hours) {
  const end = new Date();
  const start = new Date(end.getTime() - hours * 3600 * 1000);
  return {
    startTime: formatTiktokDateTime(start),
    endTime: formatTiktokDateTime(end),
  };
}

function resolveOptionValue(raw, options) {
  if (!Array.isArray(options) || !options.length) return raw;
  const hit = options.find((opt) => String(opt?.value) === String(raw));
  return hit ? hit.value : raw;
}

/**
 * 同步 / 导出时间段弹窗：确认后回传所选区间。
 * 可选 queryTypeOptions（如退货单 list 的 queryType，或导出的创建/发布/上架时间）。
 */
export default function TiktokSyncRangeModal({
  open,
  title = fixedUi('选择同步时间段'),
  defaultHours = 48,
  presets = null,
  hint = null,
  queryTypeOptions = null,
  defaultQueryType = null,
  queryTypeLabel = fixedUi("时间类型"),
  confirmText = fixedUi("开始同步"),
  confirmingLabel = null,
  confirming = false,
  onConfirm,
  onClose,
}) {
  const hasQueryType = Array.isArray(queryTypeOptions) && queryTypeOptions.length > 0;
  const initialQueryType = defaultQueryType != null && defaultQueryType !== ''
    ? defaultQueryType
    : (hasQueryType ? queryTypeOptions[0].value : null);

  const [startTime, setStartTime] = useState(() => buildRange(defaultHours).startTime);
  const [endTime, setEndTime] = useState(() => buildRange(defaultHours).endTime);
  const [queryType, setQueryType] = useState(initialQueryType);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [open]);

  const presetButtons = useMemo(() => {
    if (Array.isArray(presets) && presets.length) return presets;
    return [
      { label: fixedUi('近 24 小时'), hours: 24 },
      { label: fixedUi('近 48 小时'), hours: 48 },
      { label: fixedUi('近 7 天'), hours: 24 * 7 },
      { label: fixedUi('近 14 天'), hours: 24 * 14 },
      { label: fixedUi('近 30 天'), hours: 24 * 30 },
    ];
  }, [presets]);

  useEffect(() => {
    if (!open) return;
    const range = buildRange(defaultHours);
    setStartTime(range.startTime);
    setEndTime(range.endTime);
    setQueryType(initialQueryType);
  }, [open, defaultHours, initialQueryType]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !confirming && !submitting) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, confirming, submitting, onClose]);

  if (!open) return null;

  const applyPreset = (hours) => {
    const range = buildRange(hours);
    setStartTime(range.startTime);
    setEndTime(range.endTime);
  };

  const handleConfirm = async () => {
    if (confirming || submittingRef.current) return;
    if (!startTime || !endTime) {
      showToast(fixedUi("请选择有效的开始和结束时间"), 'error');
      return;
    }
    const startMs = new Date(String(startTime).replace(' ', 'T')).getTime();
    const endMs = new Date(String(endTime).replace(' ', 'T')).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs >= endMs) {
      showToast(fixedUi("开始时间必须早于结束时间"), 'error');
      return;
    }
    const payload = { startTime, endTime };
    if (hasQueryType && queryType != null && queryType !== '') {
      payload.queryType = queryType;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onConfirm?.(payload);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const busy = confirming || submitting;
  const confirmBtnLabel = busy
    ? (confirmingLabel || `${String(confirmText || '处理').replace(/…$/, '')}中…`)
    : confirmText;

  return createPortal(
    <div className="tko-batch-modal" role="presentation">
      <button
        type="button"
        className="tko-batch-modal__backdrop"
        aria-label={fixedUi("关闭")}
        disabled={busy}
        onClick={() => !busy && onClose?.()}
      />
      <div
        className="tko-batch-modal__panel tko-sync-range-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tko-sync-range-modal-title"
      >
        <div className="tko-batch-modal__head">
          <h2 id="tko-sync-range-modal-title">{title}</h2>
          <button
            type="button"
            className="tko-btn tko-btn--ghost tko-btn--sm"
            disabled={busy}
            onClick={onClose}
          >
            {fixedUi("关闭")}
          </button>
        </div>
        <div className="tko-batch-modal__body">
          <div className="tko-sync-range-modal__presets" role="group" aria-label={fixedUi("快捷时间段")}>
            {presetButtons.map((item) => (
              <button
                key={item.label}
                type="button"
                className="tko-btn tko-btn--secondary tko-btn--sm"
                disabled={busy}
                onClick={() => applyPreset(item.hours)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {hint ? (
            <p className="tko-sync-range-modal__hint">{hint}</p>
          ) : null}
          <div className="tko-sync-range-modal__fields">
            {hasQueryType ? (
              <label className="tko-field tko-field--block">
                <span className="tko-field__label">{queryTypeLabel}</span>
                <select
                  className="tko-select"
                  value={queryType ?? ''}
                  disabled={busy}
                  aria-label={queryTypeLabel}
                  onChange={(e) => setQueryType(resolveOptionValue(e.target.value, queryTypeOptions))}
                >
                  {queryTypeOptions.map((opt) => (
                    <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
            ) : null}
            <div className="tko-field tko-field--block">
              <span className="tko-field__label">{fixedUi("开始")}</span>
              <TiktokDateTimePicker
                className="tko-sync-range-modal__picker"
                value={startTime}
                disabled={busy}
                allowClear={false}
                placeholder={fixedUi("请选择开始时间")}
                onChange={setStartTime}
              />
            </div>
            <div className="tko-field tko-field--block">
              <span className="tko-field__label">{fixedUi("结束")}</span>
              <TiktokDateTimePicker
                className="tko-sync-range-modal__picker"
                value={endTime}
                disabled={busy}
                allowClear={false}
                placeholder={fixedUi("请选择结束时间")}
                onChange={setEndTime}
              />
            </div>
          </div>
        </div>
        <div className="tko-batch-modal__foot">
          <button
            type="button"
            className="tko-btn tko-btn--secondary tko-btn--sm"
            disabled={busy}
            onClick={onClose}
          >
            {fixedUi("取消")}
          </button>
          <button
            type="button"
            className="tko-btn tko-btn--primary tko-btn--sm"
            disabled={busy}
            onClick={handleConfirm}
          >
            {confirmBtnLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
